import React, { useEffect, useRef, useState } from "react";
import { DockviewApi } from "dockview-react";
import { api } from "./utils/api";
import { TickerLink } from "./TickerActionMenu";
import { ClearCrossChart } from "./ClearCrossChart";
import SafeNumberInput from "./SafeNumberInput";

// ClearCross: today's tweets with 1-4 tickers (regular session), each ticker scored live by the backend
// (news_data/clearcross.py): largest per-minute price / volume sigma within T-10..T+10 min per window.

const WINDOWS = [1, 2, 3, 5, 10, 20, 30];
const PRICE_SIGMA_COLOR = "#b3261e";
const VOLUME_SIGMA_COLOR = "#b25e09";
const NEW_ITEM_BG = "#fffde7";
const POLL_MS = 1000;

interface IMove {
  p: number | null;
  z: number | null;
  off: number | null;
  dir: number | null;
  v: number | null;
  vr: number | null;
  vz: number | null;
  voff: number | null;
  final: boolean;
}

interface ITicker {
  ticker: string;
  investing_id: number | null;
  fit: boolean;
  moves: Record<string, IMove>;
}

interface IItem {
  url: string;
  source: string;
  content: string;
  published_at: number;
  tweet_minute: string;
  final: boolean;
  tickers: ITicker[];
}

interface IResponse {
  day: string;
  cursor: string;
  items: IItem[];
}

interface ClearCrossProps {
  containerApi?: DockviewApi;
}

function nyTime(ms: number, withSeconds = false): string {
  return new Date(ms).toLocaleTimeString("en-US", {
    timeZone: "America/New_York",
    hour: "2-digit",
    minute: "2-digit",
    second: withSeconds ? "2-digit" : undefined,
    hour12: false,
  });
}

interface IBest {
  z: number;
  win: number;
  off: number | null;
  dir: number | null; // price move direction in that window (+1 up / -1 down), price sigma only
}

// largest price (kind "z") or volume (kind "vz") sigma over the windows, with its window and offset from T
function maxSigma(t: ITicker, kind: "z" | "vz"): IBest | null {
  let best: IBest | null = null;
  for (const w of WINDOWS) {
    const m = t.moves[String(w)];
    const z = m?.[kind];
    if (m && z !== null && z !== undefined && (best === null || z > best.z))
      best = { z, win: w, off: kind === "z" ? m.off : m.voff, dir: kind === "z" ? m.dir : null };
  }
  return best;
}

const fmtOff = (off: number | null) => (off === null ? "" : off === 0 ? " T" : off > 0 ? ` T+${off}m` : ` T−${-off}m`);

const SigmaBadge = ({ b, color, suffix }: { b: IBest; color: string; suffix?: string }) => (
  <span className="ml-1 tabular-nums" style={{ color, fontWeight: b.z >= 3 ? 700 : 400 }}>
    {b.z.toFixed(1)}σ{suffix}
    <span className="text-gray-500 font-normal text-xs">
      {" "}
      {b.win}m{fmtOff(b.off)}
    </span>
  </span>
);

interface INotifySettings {
  notify_price: number;
  notify_volume: number;
}

// Pushover thresholds (backend clearcross_settings): watchlist tickers notify once price σ > price and volume σ > volume,
// then again at +1 / +1, +2 / +2, ...
const NotifySettings = () => {
  const [saved, setSaved] = useState<INotifySettings | null>(null);
  const [draft, setDraft] = useState<INotifySettings | null>(null);
  const [status, setStatus] = useState("");

  useEffect(() => {
    api
      .get<INotifySettings>("/clearcross-settings")
      .then(({ data }) => {
        setSaved(data);
        setDraft(data);
      })
      .catch(() => setStatus("settings not loaded"));
  }, []);

  if (!draft || !saved) return <span className="text-xs text-gray-400">{status}</span>;
  const dirty = draft.notify_price !== saved.notify_price || draft.notify_volume !== saved.notify_volume;
  const valid = draft.notify_price > 0 && draft.notify_volume > 0;

  const save = () =>
    api
      .put<INotifySettings>("/clearcross-settings", draft)
      .then(({ data }) => {
        setSaved(data);
        setDraft(data);
        setStatus("saved");
        setTimeout(() => setStatus(""), 2000);
      })
      .catch(() => setStatus("save failed"));

  return (
    <form
      className="flex flex-wrap items-center gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        if (dirty && valid) save();
      }}
      title="Pushover for watchlist tickers: price σ and volume σ above these, then again at every +1σ / +1σ"
    >
      <span className="whitespace-nowrap">Notify: price σ &gt;</span>
      <SafeNumberInput
        value={draft.notify_price}
        onChange={(v) => setDraft({ ...draft, notify_price: v })}
        min={0.5}
        step={0.5}
        className="border border-gray-300 rounded px-1 tabular-nums"
        style={{ width: "4rem" }} // terminal.css would make it full width on phones
      />
      <span className="whitespace-nowrap">vol σ &gt;</span>
      <SafeNumberInput
        value={draft.notify_volume}
        onChange={(v) => setDraft({ ...draft, notify_volume: v })}
        min={0.5}
        step={0.5}
        className="border border-gray-300 rounded px-1 tabular-nums"
        style={{ width: "4rem" }} // terminal.css would make it full width on phones
      />
      <button type="submit" className="btn btn-default text-xs" disabled={!dirty || !valid}>
        Save
      </button>
      {status && <span className="text-xs text-gray-500">{status}</span>}
    </form>
  );
};

export const ClearCross = ({ containerApi }: ClearCrossProps) => {
  const [items, setItems] = useState<Record<string, IItem>>({});
  const [newUrls, setNewUrls] = useState<Set<string>>(new Set());
  // tweets whose chart the user opened (charts are fetched only once opened)
  const [openCharts, setOpenCharts] = useState<Set<string>>(new Set());
  const toggleChart = (url: string) =>
    setOpenCharts((s) => {
      const n = new Set(s);
      if (n.has(url)) n.delete(url);
      else n.add(url);
      return n;
    });
  const [lastUpdate, setLastUpdate] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cursor = useRef<string | null>(null);
  const day = useRef<string | null>(null);
  const initialLoadDone = useRef(false);
  const inFlight = useRef(false);

  useEffect(() => {
    const poll = () => {
      if (inFlight.current) return;
      inFlight.current = true;
      api
        .get<IResponse>("/clearcross", { params: cursor.current ? { since: cursor.current } : {} })
        .then(({ data }) => {
          const reset = day.current !== null && day.current !== data.day;
          day.current = data.day;
          cursor.current = data.cursor;
          setError(null);
          setLastUpdate(Date.now());
          setItems((prev) => {
            const next: Record<string, IItem> = reset ? {} : { ...prev };
            const fresh: string[] = [];
            for (const it of data.items) {
              if (!next[it.url]) fresh.push(it.url);
              next[it.url] = it;
            }
            if (initialLoadDone.current && fresh.length > 0) {
              setNewUrls((s) => {
                const n = new Set(s);
                fresh.forEach((u) => n.add(u));
                return n;
              });
            }
            return next;
          });
          initialLoadDone.current = true;
        })
        .catch((e) => setError(String(e?.message ?? e)))
        .finally(() => {
          inFlight.current = false;
        });
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, []);

  const list = Object.values(items).sort(
    (a, b) => b.published_at - a.published_at || a.url.localeCompare(b.url)
  );

  return (
    <div className="h-full flex flex-col overflow-hidden font-sans">
      <div className="flex-shrink-0 flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-1.5 border-b border-gray-200 text-sm text-gray-600">
        <span title="Tweets with 1–4 tickers, 09:30–16:00 ET · largest per-minute σ in T−10…T+10 min over the 1–30 min windows">
          Tweets with 1–4 tickers, 09:30–16:00 ET
        </span>
        <NotifySettings />
        <span className="ml-auto tabular-nums">
          {error ? <span className="text-red-600">{error}</span> : lastUpdate ? `updated ${nyTime(lastUpdate, true)}` : "loading…"}
        </span>
      </div>

      <div className="flex-1 overflow-auto">
        {list.length === 0 && <p className="text-sm text-gray-500 p-2">No tweets with tickers today yet.</p>}
        {list.map((item) => (
          <div
            key={item.url}
            className="p-2 border-b border-gray-100"
            style={newUrls.has(item.url) ? { backgroundColor: NEW_ITEM_BG } : undefined}
            onMouseEnter={
              newUrls.has(item.url)
                ? () =>
                    setNewUrls((s) => {
                      const n = new Set(s);
                      n.delete(item.url);
                      return n;
                    })
                : undefined
            }
          >
            <p className="text-sm text-gray-600">
              <span className="font-semibold">{item.source}</span>
              {" · "}
              {nyTime(item.published_at * 1000, true)}
              {!item.final && (
                <span className="ml-2 px-1.5 rounded text-xs font-semibold text-white" style={{ backgroundColor: PRICE_SIGMA_COLOR }}>
                  LIVE
                </span>
              )}
              <a href={item.url} target="_blank" rel="noreferrer" className="ml-2 text-gray-400 hover:underline">
                ↗
              </a>
              {item.tickers.length > 0 && (
                <button
                  type="button"
                  onClick={() => toggleChart(item.url)}
                  className="ml-3 text-gray-500 hover:underline cursor-pointer"
                  title={openCharts.has(item.url) ? "Hide the chart" : "Show (and fetch) the chart"}
                >
                  {openCharts.has(item.url) ? "▾ chart" : "▸ chart"}
                </button>
              )}
            </p>
            <p>
              {item.tickers.map((t) => {
                const price = maxSigma(t, "z");
                const vol = maxSigma(t, "vz");
                return (
                  <span key={t.ticker} className="mr-4">
                    <TickerLink ticker={t.ticker} containerApi={containerApi} className="font-bold cursor-pointer" />
                    {price && <SigmaBadge b={price} color={PRICE_SIGMA_COLOR} />}
                    {vol && <SigmaBadge b={vol} color={VOLUME_SIGMA_COLOR} suffix=" vol" />}
                    {!price && !vol && !t.fit && <span className="ml-1 text-xs text-gray-400">no fit</span>}
                  </span>
                );
              })}
            </p>
            <p className="whitespace-pre-wrap break-words font-normal text-black sm:pl-6">{item.content}</p>
            {item.tickers.length > 0 && (
              <ClearCrossChart
                tickers={item.tickers.map((t) => ({
                  ticker: t.ticker,
                  investing_id: t.investing_id,
                  dir: maxSigma(t, "z")?.dir ?? null,
                }))}
                containerApi={containerApi}
                show={openCharts.has(item.url)}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
